<script setup lang="ts">
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
    isOpen: boolean;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    dropdownClass?: string;
    dropdownContentClass?: string;
}>();

defineOptions({
    inheritAttrs: false
});

const emit = defineEmits(['update:isOpen']);

const isMounted = ref(false);
const buttonRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);

const { floatingStyles, update } = useFloating(buttonRef, popoverRef, {
    placement: 'bottom-end',
    middleware: [offset(0), flip(), shift()],
    whileElementsMounted: autoUpdate
});

function handleClickOutside(event: MouseEvent) {
    if (
        buttonRef.value
        && popoverRef.value
        && !buttonRef.value.contains(event.target as Node)
        && !popoverRef.value.contains(event.target as Node)
    ) {
        emit('update:isOpen', false);
    }
}

watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
        update(); // Обновляем позицию при открытии
        document.addEventListener('click', handleClickOutside);
    }
    else {
        document.removeEventListener('click', handleClickOutside);
    }
});

onMounted(() => {
    isMounted.value = true;
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div ref="buttonRef"
       class="position-relative"
       :class="dropdownClass">
    <slot name="toggle"></slot>
  </div>

  <Teleport to="body">
    <div v-if="isMounted && isOpen"
         ref="popoverRef"
         :style="floatingStyles"
         class="dropdown-content"
         :class="dropdownContentClass"
         @click.stop>
      <slot name="content"></slot>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.dropdown-content {
  position: absolute;
  z-index: 100;
  padding: 10px;
}
</style>
